import { config } from "../config";

type GetIdToken = () => Promise<string | null>;

export type Pricing = { unit_amount: number; currency: string; display: string };

let _pricingPromise: Promise<Pricing> | null = null;

export function getPricing(): Promise<Pricing> {
  if (!_pricingPromise) {
    _pricingPromise = fetch(`${config.apiUrl}/pricing`)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .catch(e => {
        _pricingPromise = null; // allow retry on next mount
        throw e;
      });
  }
  return _pricingPromise;
}

export type PublicStats = { videos_made: number };

let _statsPromise: Promise<PublicStats> | null = null;

export function getPublicStats(): Promise<PublicStats> {
  if (!_statsPromise) {
    _statsPromise = fetch(`${config.apiUrl}/public/stats`)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .catch(e => {
        _statsPromise = null;
        throw e;
      });
  }
  return _statsPromise;
}

export class ApiError extends Error {
  code?: string;
  category?: string;
  status: number;
  constructor(message: string, status: number, code?: string, category?: string) {
    super(message);
    this.status = status;
    this.code = code;
    this.category = category;
  }
}

export function createApi(getIdToken: GetIdToken) {
  async function request(path: string, init: RequestInit = {}) {
    const token = await getIdToken();
    const headers = new Headers(init.headers);
    headers.set("Content-Type", "application/json");
    if (token) headers.set("Authorization", token);

    const res = await fetch(`${config.apiUrl}${path}`, { ...init, headers });
    const body = await res.text();
    const json = body ? JSON.parse(body) : {};
    if (!res.ok) {
      throw new ApiError(
        json.error || `HTTP ${res.status}`,
        res.status,
        json.code,
        json.category,
      );
    }
    return json;
  }

  return {
    requestUpload: (contentType: string) =>
      request("/uploads", { method: "POST", body: JSON.stringify({ content_type: contentType }) }) as
        Promise<{ photo_key: string; upload_url: string }>,

    createJob: (photoKey: string, subjectHint?: string) =>
      request("/jobs", {
        method: "POST",
        body: JSON.stringify({ photo_key: photoKey, subject_hint: subjectHint }),
      }) as Promise<{ job_id: string; checkout_url: string }>,

    getJob: (jobId: string) =>
      request(`/jobs/${jobId}`) as
        Promise<{
          job_id: string;
          status: "pending" | "paid" | "processing" | "ready" | "failed";
          video_url?: string;
          download_url?: string;
          error?: string;
          created_at: number;
          parent_job_id?: string;
          variation_index?: number;
          variations_remaining: number;
        }>,

    listJobs: () =>
      request("/jobs") as Promise<{
        jobs: Array<{
          job_id: string;
          status: "pending" | "paid" | "processing" | "ready" | "failed";
          created_at: number;
          photo_url?: string;
          video_url?: string;
          download_url?: string;
          subject_hint?: string;
          error?: string;
          parent_job_id?: string;
          variation_index?: number;
          variation_count?: number;
        }>;
      }>,

    deleteJob: (jobId: string) =>
      request(`/jobs/${jobId}`, { method: "DELETE" }) as Promise<{ ok: true }>,

    createVariation: (jobId: string) =>
      request(`/jobs/${jobId}/variations`, { method: "POST" }) as Promise<{
        job_id: string;
        variation_index: number;
      }>,

    submitContact: (payload: {
      name: string;
      email: string;
      subject?: string;
      message: string;
      website?: string;
    }) =>
      request("/contact", {
        method: "POST",
        body: JSON.stringify(payload),
      }) as Promise<{ ok: true }>,

    getAdminStats: () =>
      request("/admin/stats") as Promise<{
        user_count: number;
        job_count: number;
        jobs_by_status: Record<string, number>;
        revenue_usd: number;
        jobs_last_24h: number;
        jobs_last_7d: number;
        recent_jobs: Array<{
          job_id: string;
          status: string;
          user_id: string;
          subject_hint: string;
          created_at: number;
        }>;
      }>,

    listAdminUsers: () =>
      request("/admin/users") as Promise<{
        users: Array<{
          sub: string;
          email: string;
          email_verified: boolean;
          status: string;
          enabled: boolean;
          username: string;
          created_at: number;
          job_count: number;
        }>;
      }>,

    disableUser: (sub: string) =>
      request(`/admin/users/${sub}/disable`, { method: "POST" }) as Promise<{ ok: true }>,

    enableUser: (sub: string) =>
      request(`/admin/users/${sub}/enable`, { method: "POST" }) as Promise<{ ok: true }>,

    deleteUser: (sub: string) =>
      request(`/admin/users/${sub}`, { method: "DELETE" }) as Promise<{ ok: true }>,

    getAdminSettings: () =>
      request("/admin/settings") as Promise<{
        video_model: string;
        available_models: Array<{ id: string; label: string; description: string; audio: boolean }>;
      }>,

    setVideoModel: (model: string) =>
      request("/admin/settings", {
        method: "POST",
        body: JSON.stringify({ video_model: model }),
      }) as Promise<{ video_model: string }>,

    purchaseExtraVariations: (jobId: string) =>
      request(`/jobs/${jobId}/variations/checkout`, { method: "POST" }) as
        Promise<{ checkout_url: string }>,
  };
}

export async function uploadToS3(presignedUrl: string, file: File) {
  const res = await fetch(presignedUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });
  if (!res.ok) throw new Error(`upload failed: HTTP ${res.status}`);
}
